import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getStoredAttribution } from "@/lib/attribution";
import { RECAPTCHA_SITE_KEY } from "@/components/auth/Recaptcha";
import { toast } from "sonner";

const PHONE_RE = /^[6-9]\d{9}$/;

/**
 * Shared OTP-request + verify flow used by Login.jsx (mode=login)
 * and Signup.jsx (mode=signup). Returns state + handlers.
 */
export function useAuthFlow({ mode, onSuccess }) {
    const [step, setStep] = useState("phone"); // phone | otp
    const [phone, setPhone] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [devOtp, setDevOtp] = useState(null);
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [resendLeft, setResendLeft] = useState(0); // short resend cooldown (30s), separate from OTP expiry
    const timerRef = useRef(null);
    const resendTimerRef = useRef(null);
    const [searchParams] = useSearchParams();
    const [inviteCode, setInviteCode] = useState("");
    const [invitePreview, setInvitePreview] = useState(null);
    const [accountPreview, setAccountPreview] = useState(null); // {first_name, avatar} once OTP is sent on login
    const [captchaToken, setCaptchaToken] = useState("");
    const [captchaResetSignal, setCaptchaResetSignal] = useState(0);
    const [captchaRequired, setCaptchaRequired] = useState(true);
    const [rememberMe, setRememberMe] = useState(true); // 30-day session by default

    // Adaptive gate: ask the backend whether THIS phone/mode needs the checkbox.
    // Trusted returning users get captchaRequired=false and skip it entirely.
    useEffect(() => {
        if (!RECAPTCHA_SITE_KEY) { setCaptchaRequired(false); return; }
        if (!PHONE_RE.test(phone)) { setCaptchaRequired(true); return; }
        let active = true;
        const id = setTimeout(async () => {
            try {
                const { data } = await api.post("/auth/captcha-status", { phone: `+91${phone}`, mode });
                if (active) setCaptchaRequired(!!data.required);
            } catch (_) {
                if (active) setCaptchaRequired(true); // fail-safe: show the checkbox
            }
        }, 400);
        return () => { active = false; clearTimeout(id); };
    }, [phone, mode]);

    const applyInvite = useCallback(async (raw) => {
        const c = (raw || "").trim().toUpperCase();
        if (!c) return;
        try {
            const { data } = await api.get(`/invites/code/${c}`);
            setInviteCode(c);
            setInvitePreview(data);
            toast.success(
                `Invite found — you'll join as ${data.person_name || "a family member"}.`
            );
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) || "Invalid invite code."
            );
        }
    }, []);

    useEffect(() => {
        const c = searchParams.get("invite");
        if (c) applyInvite(c);
    }, [searchParams, applyInvite]);

    useEffect(() => () => { clearInterval(timerRef.current); clearInterval(resendTimerRef.current); }, []);

    const startTimer = (seconds) => {
        setSecondsLeft(seconds);
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setSecondsLeft((s) => {
                if (s <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
    };

    const startResendTimer = (seconds) => {
        setResendLeft(seconds);
        clearInterval(resendTimerRef.current);
        resendTimerRef.current = setInterval(() => {
            setResendLeft((s) => {
                if (s <= 1) {
                    clearInterval(resendTimerRef.current);
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
    };

    const requestOtp = async (e) => {
        e?.preventDefault?.();
        const isResend = step === "otp"; // resend from the OTP screen skips captcha
        if (!PHONE_RE.test(phone)) {
            toast.error("Enter a valid 10-digit Indian mobile (starts 6–9).");
            return;
        }
        if (mode === "signup" && !isResend && name.trim().length < 2) {
            toast.error("Please enter your full name (at least 2 characters).");
            return;
        }
        if (!isResend && RECAPTCHA_SITE_KEY && captchaRequired && !captchaToken) {
            toast.error("Please complete the captcha to continue.");
            return;
        }
        setSending(true);
        try {
            // On the LOGIN flow, try to fetch a minimal profile preview first
            // so the OTP screen can greet returning users by first-name + avatar.
            if (mode === "login") {
                try {
                    const { data: lookup } = await api.post("/auth/lookup", { phone: `+91${phone}` });
                    setAccountPreview(lookup);
                } catch (_) { setAccountPreview(null); }
            } else {
                setAccountPreview(null);
            }
            const { data } = await api.post("/auth/request-otp", {
                phone: `+91${phone}`,
                mode: isResend ? undefined : mode,
                captcha_token: isResend ? undefined : (captchaRequired ? captchaToken || undefined : undefined),
            });
            setStep("otp");
            setCode("");
            setDevOtp(data.dev_otp || null);
            startTimer(data.expires_in_seconds || 300);
            startResendTimer(30);
            toast.success("OTP sent");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) || "Failed to send OTP."
            );
        } finally {
            setSending(false);
            // reCAPTCHA v2 tokens are single-use — clear + reset the widget after
            // every phone-step submit so a fresh check is required next time.
            if (!isResend) {
                setCaptchaToken("");
                setCaptchaResetSignal((s) => s + 1);
            }
        }
    };

    const verifyOtp = async (e) => {
        e?.preventDefault?.();
        if (!/^\d{6}$/.test(code)) {
            toast.error("Enter the 6-digit OTP.");
            return;
        }
        setVerifying(true);
        try {
            const { data } = await api.post("/auth/verify-otp", {
                phone: `+91${phone}`,
                code,
                name: mode === "signup" ? name.trim() : undefined,
                email: mode === "signup" ? (email.trim() || undefined) : undefined,
                invite_code: invitePreview ? inviteCode : undefined,
                mode: invitePreview ? undefined : mode,
                remember_me: rememberMe,
                attribution: mode === "signup" ? (getStoredAttribution() || undefined) : undefined,
            });
            onSuccess?.(data.user);
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed."
            );
        } finally {
            setVerifying(false);
        }
    };

    const changeNumber = () => {
        setStep("phone");
        setCode("");
        setDevOtp(null);
        clearInterval(timerRef.current);
        clearInterval(resendTimerRef.current);
        setResendLeft(0);
    };

    return {
        step, phone, setPhone, name, setName, email, setEmail, code, setCode, devOtp,
        sending, verifying, secondsLeft, resendLeft, invitePreview, inviteCode, accountPreview,
        setInviteCode, setInvitePreview, applyInvite,
        requestOtp, verifyOtp, changeNumber,
        captchaToken, setCaptchaToken, captchaResetSignal, captchaRequired,
        rememberMe, setRememberMe,
    };
}
